/** Dropdown de usuario en el header: nombre, cambio de sucursal u "elegir espacio de trabajo" (multi empresa/sucursal), cambio de módulo, toggle de dark mode, último ingreso y cierre de sesión. */
import React, { useState } from "react";
import { Dropdown, Button, Divider, Select } from "antd";
import {
  MdPerson,
  MdLogout,
  MdLightMode,
  MdDarkMode,
  MdWorkspaces,
  MdBusiness,
  MdPlace,
} from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { useAuthStore, tieneMultiplesEmpresas } from "@/stores/authStore";
import { useUIStore } from "@/stores/uiStore";
import { saveInStorage, parseDate } from "@/utils";
import { POSTAccesoUltimoModulo } from "@/api";
import { useWorkSession } from "@/hooks";
import "../index.css";

interface DropDownMenuProps {
  children: React.ReactNode;
}

const DropDownMenu: React.FC<DropDownMenuProps> = ({ children }) => {
  const navigate = useNavigate();
  const { loggedUserInnerwork, activeSucursal } = useWorkSession();
  const logout = useAuthStore((s) => s.logout);
  const setActiveSucursal = useAuthStore((s) => s.setActiveSucursal);
  const updateLoggedUser = useAuthStore((s) => s.updateLoggedUser);
  const setCurrentModule = useUIStore((s) => s.setCurrentModule);
  const modules = useUIStore((s) => s.modules);
  const currentModule = useUIStore((s) => s.currentModule);
  const darkMode = useUIStore((s) => s.darkMode);
  const toggleDarkMode = useUIStore((s) => s.toggleDarkMode);
  const [open, setOpen] = useState(false);

  const handleLogout = () => {
    setOpen(false);
    setTimeout(() => {
      logout();
      navigate("/");
    }, 100);
  };

  const handleChangeModule = async (value: number) => {
    setCurrentModule(value);
    saveInStorage("lastModule", value);
    await POSTAccesoUltimoModulo(loggedUserInnerwork, value);
  };

  const handleChangeSucursal = (idSucursal: number) => {
    const sucursal = loggedUserInnerwork?.sucursales?.find((s) => s.idSucursal === idSucursal);
    if (!sucursal) return;
    // Al cambiar de sucursal puede cambiar la empresa (caso multi-empresa); actualizamos el
    // contexto para que la agenda y el resto de listados refresquen por la empresa correcta.
    updateLoggedUser({
      codEmpresa: sucursal.codEmpresa || loggedUserInnerwork.codEmpresa,
      nombreEmpresa: sucursal.nombreEmpresa || loggedUserInnerwork.nombreEmpresa,
    });
    setActiveSucursal(sucursal);
  };

  const sucursales = loggedUserInnerwork?.sucursales || [];
  // Ofrecemos el wizard de espacio de trabajo (estilo Notion) cuando hay algo que elegir:
  // varias empresas (empresa + sucursal) o varias sucursales. Se muestra junto al selector
  // de sucursal cuando hay una sola empresa.
  const multiplesEmpresas = tieneMultiplesEmpresas(loggedUserInnerwork);
  const puedeElegirEspacio = multiplesEmpresas || sucursales.length > 1;

  // Contexto actual para no quedar "en el aire" al volver a abrir el perfil.
  const nombreEmpresa = loggedUserInnerwork?.nombreEmpresa || loggedUserInnerwork?.codEmpresa || "";
  const nombreSucursal = activeSucursal?.nombre || "";

  const handleElegirEspacio = () => {
    setOpen(false);
    navigate("/select-workspace");
  };

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
      arrow={{ pointAtCenter: true }}
      trigger={["click"]}
      dropdownRender={() => (
        <div className="profile-dropdown">
          <div className="profile-user-row">
            <MdPerson size={14} color="#999" />
            <span className="profile-user-name">
              {loggedUserInnerwork?.nombre || loggedUserInnerwork?.codUsuario}
            </span>
            <Button
              type="text"
              size="small"
              icon={darkMode ? <MdLightMode size={16} /> : <MdDarkMode size={16} />}
              onClick={toggleDarkMode}
              style={{ marginLeft: "auto", color: "#8c8c8c", fontSize: "16px" }}
            />
          </div>

          {puedeElegirEspacio && (
            <>
              <div className="profile-workspace-info">
                {nombreEmpresa && (
                  <>
                    <span className="profile-workspace-item">
                      <MdBusiness size={14} />
                      {nombreEmpresa}
                    </span>
                    <span className="profile-workspace-sep">-</span>
                  </>
                )}
                <span
                  className={`profile-workspace-item${nombreSucursal ? "" : " profile-workspace-item--todas"}`}
                >
                  {nombreSucursal || "Todas las sucursales"}
                </span>
              </div>
              <div className="profile-workspace-row" onClick={handleElegirEspacio}>
                <MdWorkspaces size={16} />
                <span>Elegir espacio de trabajo</span>
              </div>
            </>
          )}

          {!multiplesEmpresas && sucursales.length > 0 && (
            <>
              {!puedeElegirEspacio && nombreEmpresa && (
                <div className="profile-workspace-info">
                  <span className="profile-workspace-item">
                    <MdBusiness size={14} />
                    {nombreEmpresa}
                  </span>
                </div>
              )}
              <div className="profile-sucursal-row">
                <Select
                  value={activeSucursal?.idSucursal}
                  onChange={handleChangeSucursal}
                  options={sucursales.map((s) => ({ label: s.nombre, value: s.idSucursal }))}
                  style={{ width: "100%" }}
                  size="middle"
                />
              </div>
            </>
          )}

          <Divider style={{ margin: "8px 0" }} />

          <Select
            value={currentModule}
            onChange={handleChangeModule}
            options={modules.map((m) => ({ label: m.label, value: m.value }))}
            style={{ width: "100%" }}
            size="middle"
          />

          <Divider style={{ margin: "8px 0" }} />

          <div className="profile-logout-row" onClick={handleLogout}>
            <MdLogout size={16} />
            <span>Cerrar sesión</span>
          </div>
          <div className="profile-last-login">
            Ult. ingreso:{" "}
            {parseDate(
              (loggedUserInnerwork?.iwkDatos as Record<string, string>)?.fechaUltimoAcceso
            ) || "-"}
          </div>
        </div>
      )}
    >
      {children}
    </Dropdown>
  );
};

export default DropDownMenu;
